import type { LucideIcon } from "lucide-react";
import type { KpiItem } from "@/shared/ui/core/KpiCard";
import type { ModuleInsightsModel } from "@/shared/ui/core/ModuleInsights";
import type { SemanticTone } from "@/shared/ui/core/types";
import { resolveStatusTone } from "@/shared/ui/StatusBadge";
import { statusLabel } from "@/shared/ui/statusLabels";
import { ensureUniqueKpiTones, orderKpisWithTotalFirst, resolveKpiTone } from "@/shared/ui/core/statusTone";

export type StatusBucket = { status: string; count: number };

export type ListInsightKpiSpec = {
  id: string;
  label: string;
  /** Sum of these status counts (or custom via `count`). */
  statuses?: readonly string[];
  count?: number;
  tone?: SemanticTone;
  icon?: LucideIcon;
  hint?: string;
  onClick?: () => void;
  /** Explicitly mark card as selected. If omitted, inferred from selectedStatus. */
  selected?: boolean;
  /** When true, tone becomes danger/warning if count > 0 else neutral. */
  alertTone?: "danger" | "warning";
};

function n(v: number): string {
  return v.toLocaleString("vi-VN");
}

/** Tally status field from loaded rows (client-full lists only). */
export function tallyStatusBuckets<T>(
  rows: readonly T[],
  getStatus: (row: T) => string,
): StatusBucket[] {
  const map = new Map<string, number>();
  for (const row of rows) {
    const s = getStatus(row);
    if (!s) continue;
    map.set(s, (map.get(s) ?? 0) + 1);
  }
  return [...map.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([status, count]) => ({ status, count }));
}

export function countInBuckets(
  buckets: readonly StatusBucket[],
  statuses: readonly string[],
): number {
  const set = new Set(statuses);
  return buckets.reduce((sum, b) => (set.has(b.status) ? sum + b.count : sum), 0);
}

/** Build ModuleInsights from status buckets + KPI specs (shared list pattern). */
export function buildListInsights(args: {
  buckets: readonly StatusBucket[];
  kpis: readonly ListInsightKpiSpec[];
  distributionTitle: string;
  /** Status code currently filtering the list. `"all"` selects the total KPI. */
  selectedStatus?: string;
  onSegmentClick?: (status: string) => void;
}): ModuleInsightsModel {
  const { buckets, kpis, distributionTitle, selectedStatus, onSegmentClick } = args;

  const mapped: KpiItem[] = kpis.map((spec) => {
    const value =
      spec.count ??
      (spec.statuses ? countInBuckets(buckets, spec.statuses) : 0);
    const selected = spec.selected ?? isInsightSelected(spec.id, selectedStatus, spec.statuses);
    let tone = spec.tone;
    if (spec.alertTone) {
      tone = value > 0 ? spec.alertTone : (spec.tone ?? "draft");
    }
    tone = resolveKpiTone({ id: spec.id, label: spec.label, tone });
    return {
      id: spec.id,
      label: spec.label,
      value: n(value),
      hint: spec.hint,
      tone,
      icon: spec.icon,
      onClick: spec.onClick,
      selected,
    };
  });

  return {
    kpis: ensureUniqueKpiTones(mapped),
    distribution: {
      title: distributionTitle,
      segments: buckets.map((b) => ({
        id: b.status,
        label: statusLabel(b.status),
        count: b.count,
        tone: resolveStatusTone(b.status),
        selected: selectedStatus === b.status,
        onClick: onSegmentClick ? () => onSegmentClick(b.status) : undefined,
      })),
    },
  };
}

function isInsightSelected(
  kpiId: string,
  selectedStatus: string | undefined,
  statuses?: readonly string[],
): boolean {
  if (selectedStatus === undefined) return false;
  const sel = selectedStatus.toLowerCase().replace(/[\s_-]+/g, "");
  const id = kpiId.toLowerCase().replace(/[\s_-]+/g, "");
  if (sel === "all" || sel === "") {
    return id === "total" || id === "all";
  }
  if (id === sel) return true;
  if (
    (id === "pending" || id === "pendingapproval") &&
    (sel === "pending" || sel === "pendingapproval")
  ) {
    return true;
  }
  if (statuses && statuses.some((s) => s.toLowerCase().replace(/[\s_-]+/g, "") === sel)) return true;
  return false;
}

/** Convenience: rows → insights (client-full lists). */
export function buildListInsightsFromRows<T>(args: {
  rows: readonly T[];
  getStatus: (row: T) => string;
  kpis: readonly ListInsightKpiSpec[];
  distributionTitle: string;
}): ModuleInsightsModel {
  return buildListInsights({
    buckets: tallyStatusBuckets(args.rows, args.getStatus),
    kpis: args.kpis,
    distributionTitle: args.distributionTitle,
  });
}
